import {
  ThemeColors,
  TinyDropdownMenu,
  Toolbar,
} from '@octopus-do/octopus-storybook'
import { TagLight } from '@octopus-do/octopus-storybook/icons'
import type React from 'react'
import { forwardRef } from 'react'

import { MENU_OFFSET } from '../../constants'
import { type TagOption, useTagOptions } from './hooks'

// Key the menu reports to Toolbar.Root; the toggled tag id follows it.
export const TAGS_MENU_ID = 'tags'

interface ContentProps {
  options: TagOption[]
}

// A checker per tag with the count of its pages, as the page tags picker
// without its row menus and the new tag field (figma 86193:888293).
const Content: React.FC<ContentProps> = ({ options }) => (
  <TinyDropdownMenu.Group>
    {options.map(({ checked, color, count, id, title }) => (
      <TinyDropdownMenu.Button
        checker={{ checked, color }}
        counter={count}
        id={id}
        key={id}
        label={title}
      />
    ))}
  </TinyDropdownMenu.Group>
)

interface TriggerProps {
  disabled: boolean
  // Without tags there is nothing to filter by; the tooltip says so. It still
  // shows on the disabled button because the tooltip trigger wraps it.
  empty: boolean
  // The plan has no tags: the button looks off but sells the plan on click,
  // like the tags button of the page toolbar.
  locked: boolean
  onUpgrade: () => void
  // A filter is on: the mode look, like the view toggles next to it.
  selected: boolean
}

const Trigger = forwardRef<HTMLButtonElement, TriggerProps>(
  ({ disabled, empty, locked, onUpgrade, selected }, ref) => {
    const { active, toggle } = TinyDropdownMenu.useTrigger()

    return (
      <Toolbar.DropdownButton
        active={active}
        disabled={disabled || (!locked && empty)}
        icon={TagLight}
        locked={locked}
        onClick={locked ? onUpgrade : toggle}
        ref={ref}
        selected={selected}
        tooltip={!locked && empty ? 'No tags yet' : 'Filter by tags'}
      />
    )
  }
)

interface Props {
  disabled: boolean
  locked: boolean
  onUpgrade: () => void
  selected: boolean
}

// The filter is a multi-select, so the menu stays open while tags are toggled.
export const TagsFilterMenu: React.FC<Props> = ({
  disabled,
  locked,
  onUpgrade,
  selected,
}) => {
  const { onClick } = Toolbar.useToolbarClick(TAGS_MENU_ID)
  const options = useTagOptions()

  return (
    <TinyDropdownMenu.Root
      content={Content}
      onClick={onClick}
      options={options}
      preventClose
      sideOffset={MENU_OFFSET}
      theme={ThemeColors.light}
      width="grow"
    >
      <Trigger
        disabled={disabled}
        empty={options.length === 0}
        locked={locked}
        onUpgrade={onUpgrade}
        selected={selected}
      />
    </TinyDropdownMenu.Root>
  )
}
